<template>
  <div class="flex flex-col">
    <div v-if="showMultiDepartmentUsersTable" class="px-6 pt-2 pb-2">
      <p class="text-[0.78rem] font-semibold text-[#1e293b] mb-2">Users who has multiple department</p>
      <div class="w-full overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table class="ead-multi-dept-table w-full min-w-[520px] border-collapse text-sm">
          <thead>
            <tr>
              <th>Full Name</th>
              <th>User name</th>
              <th>Sex</th>
              <th>Role</th>
              <th class="num">Salary</th>
              <th class="select-col">Select</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in multiDeptUsers"
              :key="row.id"
              class="hover:bg-slate-50 transition-colors border-t border-slate-200"
            >
              <td>{{ row.fullName || '—' }}</td>
              <td>{{ row.username || '—' }}</td>
              <td>{{ row.sex || '—' }}</td>
              <td>{{ row.role || '—' }}</td>
              <td class="num">{{ fmtSalary(row.salary) }}</td>
              <td class="select-col">
                <input
                  type="checkbox"
                  :checked="isMultiDeptUserChecked(row.id)"
                  class="h-4 w-4 cursor-pointer accent-[#0d4169]"
                  @change="toggleMultiDeptUser(row.id, $event.target.checked)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <form class="px-6 pt-2" @submit.prevent="submitEntry">
      <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2 items-end">
        <div class="flex flex-col gap-1">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Beneficiary</label>
          <select
            v-model="form.beneficiary"
            class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
            required
          >
            <option value="" disabled>Select Beneficiary</option>
            <option value="earn">Earn</option>
            <option value="deduct">Deduct</option>
          </select>
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Name</label>
          <select
            v-model="form.name"
            :disabled="!form.beneficiary || isLoadingTypes"
            class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-slate-100"
            required
          >
            <option value="" disabled>{{ nameSelectPlaceholder }}</option>
            <option
              v-for="option in resolvedNameOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Type and amount</label>
          <div class="flex rounded-md border border-[#cbd5e1] overflow-hidden bg-white focus-within:border-[#0d4169] transition-colors">
            <select
              v-model="form.amountType"
              class="px-2.5 py-1.5 border-0 border-r border-[#cbd5e1] outline-none text-sm bg-white cursor-pointer min-w-[4.5rem] shrink-0"
              required
            >
              <option value="" disabled>Type</option>
              <option value="fixed">Fixed</option>
              <option value="salary_percent">Salary %</option>
            </select>
            <input
              v-model="form.amount"
              placeholder="Amount"
              class="flex-1 min-w-0 px-2.5 py-1.5 border-0 outline-none text-sm"
              required
            />
          </div>
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Applicable for</label>
          <select
            v-model="form.applicableFor"
            class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
            required
          >
            <option value="" disabled>Select applicable</option>
            <option value="one_time">One Time</option>
            <option value="all_months">All Months</option>
          </select>
        </div>
      </div>

      <div v-if="filterForm.selectedMonth" class="flex justify-center py-6">
        <button
          type="submit"
          :disabled="!canPrepareEarnDeduct"
          class="min-w-[120px] px-8 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {{ isEditing ? 'Update' : 'Add' }}
        </button>
      </div>
    </form>

    <div v-if="entries.length > 0" class="px-6 pb-2">
      <div class="w-full overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table class="ead-entries-table w-full border-collapse text-sm">
          <colgroup>
            <col class="ead-col" />
            <col class="ead-col" />
            <col class="ead-col" />
            <col class="ead-col" />
            <col class="ead-col" />
            <col class="ead-col" />
          </colgroup>
          <thead>
            <tr>
              <th>Beneficiary</th>
              <th>Name</th>
              <th>Type</th>
              <th>Amount</th>
              <th>Applicable for</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in entries"
              :key="row.id"
              class="hover:bg-slate-50 transition-colors border-t border-slate-200"
            >
              <td>{{ row.beneficiaryLabel }}</td>
              <td>{{ row.nameLabel }}</td>
              <td>{{ row.amountTypeLabel }}</td>
              <td>{{ row.amountDisplay }}</td>
              <td>{{ row.applicableForLabel }}</td>
              <td class="actions">
                <div class="flex items-center gap-1">
                  <button
                    type="button"
                    class="ead-editbtn hover:underline text-emerald-600"
                    @click="editEntry(row)"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    class="ead-delbtn p-1 text-red-500 hover:text-red-600 hover:bg-red-50 rounded"
                    title="Delete"
                    @click="deleteEntry(row.id)"
                  >
                    Delete
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="filterForm.selectedMonth" class="flex justify-center py-6">
        <button
          type="button"
          :disabled="!canPrepareEarnDeduct || isSaving"
          class="min-w-[120px] px-8 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          @click="saveAll"
        >
          {{ isSaving ? 'Saving...' : 'Save' }}
        </button>
      </div>
    </div>

    <div v-if="canLoadSavedEntries" class="px-6 pb-2">
      <div v-if="isLoadingSaved" class="py-6 flex justify-center items-center">
        <div class="h-8 w-8 border-4 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
      </div>
      <div v-else class="w-full overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table class="ead-entries-table ead-saved-table w-full border-collapse text-sm">
          <colgroup>
            <col class="ead-saved-col" />
            <col class="ead-saved-col" />
            <col class="ead-saved-col" />
            <col class="ead-saved-col" />
            <col class="ead-saved-col" />
            <col class="ead-saved-col" />
          </colgroup>
          <thead>
            <tr>
              <th>Full Name</th>
              <th>Beneficiary</th>
              <th>Name</th>
              <th>Type</th>
              <th>Amount</th>
              <th>Applicable for</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in sortedSavedEntries"
              :key="row.id"
              class="hover:bg-slate-50 transition-colors border-t border-slate-200"
            >
              <td>{{ row.fullName || '—' }}</td>
              <td>{{ row.beneficiaryLabel }}</td>
              <td>{{ row.definitionName || '—' }}</td>
              <td>{{ row.amountTypeLabel }}</td>
              <td>{{ row.amountDisplay }}</td>
              <td>{{ row.applicableForLabel }}</td>
            </tr>
            <tr v-if="sortedSavedEntries.length === 0">
              <td colspan="6" class="ead-empty-cell">No saved entries found for the selected filters.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="px-6 pb-4 pt-4 mt-2">
      <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <span
          :title="preparedByTooltip || undefined"
          class="ead-prepared-by-label px-2 py-2 text-xs font-semibold text-slate-700 bg-slate-200/80 rounded-md text-center cursor-default select-none"
        >
          {{ preparedByButtonLabel }}
        </span>
        <button
          v-for="action in otherApprovalActions"
          :key="action.key"
          type="button"
          :disabled="!action.canClick || isUpdatingApproval"
          :title="action.tooltip || undefined"
          class="px-2 py-2 text-xs font-semibold text-slate-700 bg-slate-200/80 rounded-md transition-colors text-center disabled:opacity-50 disabled:cursor-not-allowed"
          :class="action.canClick && !isUpdatingApproval ? 'hover:bg-slate-300' : 'cursor-default'"
          @click="onApprovalAction(action.key)"
        >
          {{ action.label }}
        </button>
      </div>
    </div>

    <FinanceToast :show="toast.show" :variant="toast.variant">{{ toast.message }}</FinanceToast>
  </div>
</template>

<script setup>
import { computed, defineEmits, defineProps, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { apiFetch } from '../../../utils/api'
import FinanceToast from '../FinanceToast.vue'
import { useMonthlyConfigurationFilters, resolveSelectEntityForScope } from './useMonthlyConfigurationFilters.js'
import { buildEarnDeductFilterContext } from './useMonthlyConfigurationFilters.js'
import { ALL_BRANCHES_SCOPE_VALUE } from './useMonthlyConfigurationBranches.js'
import {
  resolveEarnDeductPeriodKey,
  clearEarnDeductCurrentPeriod,
  persistEarnDeductPeriodFilters
} from './useEarnDeductLocalStorage.js'

const props = defineProps({
  entries: {
    type: Array,
    default: () => []
  },
  apiBase: {
    type: String,
    default: '/finance'
  },
  skipPrivilegeCheck: {
    type: Boolean,
    default: false
  },
  privilegeName: {
    type: String,
    default: 'Earn And Deduct'
  }
})

const earnDeductTypeApiPath = computed(() => `${props.apiBase}/earn-deduct-type`)
const earnAndDeductConfigApiPath = computed(() => `${props.apiBase}/earn-and-deduct-config`)

const emit = defineEmits(['update:entries'])

const store = useStore()
const currentUser = computed(() => store.getters.currentUser || store.state.user || {})

const userPrivileges = computed(() => {
  const raw = currentUser.value?.privilege
  if (!raw) return {}
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw)
    } catch {
      return {}
    }
  }
  return raw
})

const hasAllPrivilege = computed(() => {
  return userPrivileges.value?.All === true || userPrivileges.value?.all === true
})

function getPrivilegeEntry(key) {
  const priv = userPrivileges.value || {}
  if (!key) return null
  if (priv?.[key]) return priv[key]

  const normalizedNeedle = String(key).trim().toLowerCase()
  const foundKey = Object.keys(priv).find((k) => String(k).trim().toLowerCase() === normalizedNeedle)
  return foundKey ? priv[foundKey] : null
}

function getActionFlag(entry, action) {
  if (!entry || typeof entry !== 'object') return false
  if (!action) return false
  if (entry[action] !== undefined) return !!entry[action]

  const a = String(action)
  const candidates = [
    a.toLowerCase(),
    a.toUpperCase(),
    a.charAt(0).toUpperCase() + a.slice(1).toLowerCase()
  ]
  for (const k of candidates) {
    if (entry[k] !== undefined) return !!entry[k]
  }
  return false
}

const canPrepareEarnDeduct = computed(() => {
  if (props.skipPrivilegeCheck) return true
  if (hasAllPrivilege.value) return true
  return getActionFlag(getPrivilegeEntry(props.privilegeName), 'Prepared By')
})

const {
  form: filterForm,
  searchResults,
  isSearchingUsers
} = useMonthlyConfigurationFilters()

const otherApprovalActions = computed(() => {
  const checkedApprovers = uniqueApproversByField('checkedBy', 'checkedByFullName')
  const crossCheckedApprovers = uniqueApproversByField('crossCheckedBy', 'crossCheckedByFullName')
  const approvedApprovers = uniqueApproversByField('approvedBy', 'approvedByFullName')

  return [
    {
      key: 'checked',
      baseLabel: 'Checked By',
      label: buildApprovalButtonLabel('Checked By', checkedApprovers),
      tooltip: buildApprovalTooltip(checkedApprovers),
      canClick: canPerformApprovalAction('Checked By') && uniquePreparers.value.length > 0 && sortedSavedEntries.value.length > 0
    },
    {
      key: 'cross_checked',
      baseLabel: 'Cross Checked By',
      label: buildApprovalButtonLabel('Cross Checked By', crossCheckedApprovers),
      tooltip: buildApprovalTooltip(crossCheckedApprovers),
      canClick: canPerformApprovalAction('Cross Checked By') && checkedApprovers.length > 0 && sortedSavedEntries.value.length > 0
    },
    {
      key: 'approved',
      baseLabel: 'Approved By',
      label: buildApprovalButtonLabel('Approved By', approvedApprovers),
      tooltip: buildApprovalTooltip(approvedApprovers),
      canClick: canPerformApprovalAction('Approved By') && crossCheckedApprovers.length > 0 && sortedSavedEntries.value.length > 0
    }
  ]
})

function canPerformApprovalAction(action) {
  if (hasAllPrivilege.value) return true
  return getActionFlag(getPrivilegeEntry(props.privilegeName), action)
}

function uniqueApproversByField(idField, nameField) {
  const seen = new Set()
  const list = []
  for (const row of sortedSavedEntries.value) {
    const id = row?.[idField]
    if (!id || seen.has(id)) continue
    seen.add(id)
    list.push({
      id,
      fullName: String(row?.[nameField] || '').trim() || `User #${id}`
    })
  }
  return list
}

function buildApprovalButtonLabel(baseLabel, approvers) {
  if (!approvers.length) return baseLabel
  if (approvers.length === 1) {
    const name = firstNameFromFullName(approvers[0].fullName)
    return name ? `${baseLabel} ${name}` : baseLabel
  }
  const first = firstNameFromFullName(approvers[0].fullName)
  return first ? `${baseLabel} ${first} and others` : `${baseLabel} and others`
}

function buildApprovalTooltip(approvers) {
  if (approvers.length <= 1) return ''
  return approvers.map((p) => p.fullName).join(', ')
}

function sortSavedEntriesByFullName(rows) {
  return [...rows].sort((a, b) => {
    const aName = String(a?.fullName || '').trim()
    const bName = String(b?.fullName || '').trim()
    return aName.localeCompare(bName, undefined, { sensitivity: 'base' })
  })
}

function firstNameFromFullName(fullName) {
  const parts = String(fullName || '').trim().split(/\s+/).filter(Boolean)
  return parts[0] || ''
}

const sortedSavedEntries = computed(() => sortSavedEntriesByFullName(savedEntries.value))

const uniquePreparers = computed(() => uniqueApproversByField('preparedBy', 'preparedByFullName'))

const preparedByButtonLabel = computed(() => buildApprovalButtonLabel('Prepared By', uniquePreparers.value))

const preparedByTooltip = computed(() => buildApprovalTooltip(uniquePreparers.value))

const beneficiaryLabels = {
  earn: 'Earn',
  deduct: 'Deduct'
}

const amountTypeLabels = {
  fixed: 'Fixed',
  salary_percent: 'Salary %'
}

const applicableForLabels = {
  one_time: 'One Time',
  all_months: 'All Months'
}

const form = reactive({
  beneficiary: '',
  name: '',
  amountType: '',
  amount: '',
  applicableFor: ''
})

const earnDeductTypes = ref([])
const isLoadingTypes = ref(false)
const isSaving = ref(false)
const isUpdatingApproval = ref(false)
const isLoadingSaved = ref(false)
const savedEntries = ref([])
const editingEntryId = ref(null)
const editingEntryNameLabel = ref('')
const suppressNameReset = ref(false)
const multiDeptUsers = ref([])
const isLoadingMultiDeptUsers = ref(false)
const checkedMultiDeptUserIds = ref({})

const isEditing = computed(() => editingEntryId.value !== null)

const canLoadMultiDepartmentUsers = computed(() => {
  if (resolveSelectEntityForScope(filterForm.selectedBranchId, filterForm.selectEntity) !== 'by_department') {
    return false
  }
  return !!String(filterForm.departmentId || '').trim()
})

const showMultiDepartmentUsersTable = computed(() => {
  if (!canLoadMultiDepartmentUsers.value) {
    return false
  }
  if (isLoadingMultiDeptUsers.value) {
    return false
  }
  return multiDeptUsers.value.length > 0
})

const toast = reactive({
  show: false,
  message: '',
  variant: 'success'
})

const nameOptions = computed(() => {
  if (form.beneficiary === 'earn') {
    return earnDeductTypes.value
      .filter((row) => row.type === 'Earn' && row.active)
      .map((row) => ({
        value: String(row.id),
        label: row.description
      }))
  }
  if (form.beneficiary === 'deduct') {
    return earnDeductTypes.value
      .filter((row) => row.type === 'Deduct' && row.active)
      .map((row) => ({
        value: String(row.id),
        label: row.description
      }))
  }
  return []
})

const nameSelectPlaceholder = computed(() => {
  if (isLoadingTypes.value) return 'Loading names...'
  if (!form.beneficiary) return 'Select beneficiary first'
  if (nameOptions.value.length === 0) {
    return form.beneficiary === 'earn' ? 'No active earn types' : 'No active deduct types'
  }
  return 'Select Name'
})

const resolvedNameOptions = computed(() => {
  const options = [...nameOptions.value]
  if (!form.name) return options
  const value = String(form.name)
  if (options.some((option) => option.value === value)) {
    return options
  }
  const matchedType = earnDeductTypes.value.find((row) => String(row.id) === value)
  return [
    ...options,
    {
      value,
      label: matchedType?.description || editingEntryNameLabel.value || value
    }
  ]
})

watch(
  () => form.beneficiary,
  () => {
    if (suppressNameReset.value) return
    form.name = ''
  }
)

watch(
  () => resolveEarnDeductPeriodKey(filterForm),
  (nextKey, prevKey) => {
    if (prevKey !== undefined && nextKey !== prevKey) {
      resetFormFields()
    }
  }
)

watch(
  () => [
    filterForm.selectedMonth,
    filterForm.fromDate,
    filterForm.toDate,
    filterForm.selectedBranchId,
    filterForm.selectEntity,
    filterForm.departmentId,
    filterForm.searchName,
    isSearchingUsers.value,
    searchResults.value.length
  ],
  () => {
    scheduleLoadSavedEntries()
    scheduleLoadMultiDepartmentUsers()
  }
)

function showToast(message, variant = 'success') {
  toast.message = message
  toast.variant = variant
  toast.show = true
  window.setTimeout(() => {
    toast.show = false
    toast.variant = 'success'
  }, 4500)
}

function resetFormFields() {
  editingEntryId.value = null
  editingEntryNameLabel.value = ''
  form.beneficiary = ''
  form.name = ''
  form.amountType = ''
  form.amount = ''
  form.applicableFor = ''
}

async function populateFormFromEntry(row) {
  suppressNameReset.value = true
  form.beneficiary = row.beneficiary
  await nextTick()
  form.name = String(row.nameId)
  form.amountType = row.amountType
  form.amount = row.amount
  form.applicableFor = row.applicableFor
  await nextTick()
  suppressNameReset.value = false
}

function buildPeriodContext() {
  const periodKey = resolveEarnDeductPeriodKey(filterForm)
  if (filterForm.selectedMonth) {
    return {
      periodKey,
      periodType: 'month',
      selectedMonth: filterForm.selectedMonth,
      fromDate: null,
      toDate: null
    }
  }
  return {
    periodKey,
    periodType: 'range',
    selectedMonth: null,
    fromDate: filterForm.fromDate,
    toDate: filterForm.toDate
  }
}

function buildEntryFromForm() {
  const nameId = String(form.name)
  const selectedName = resolvedNameOptions.value.find((option) => option.value === nameId)
  return {
    ...buildPeriodContext(),
    ...buildEarnDeductFilterContext(filterForm),
    beneficiary: form.beneficiary,
    beneficiaryLabel: beneficiaryLabels[form.beneficiary] || form.beneficiary,
    nameId,
    nameLabel: selectedName?.label || editingEntryNameLabel.value || nameId,
    amountType: form.amountType,
    amountTypeLabel: amountTypeLabels[form.amountType] || form.amountType,
    amount: String(form.amount).trim(),
    amountDisplay: formatAmountDisplay(form.amountType, form.amount),
    applicableFor: form.applicableFor,
    applicableForLabel: applicableForLabels[form.applicableFor] || form.applicableFor
  }
}

function isDuplicateName(nameId, excludeId = null) {
  return props.entries.some(
    (row) => String(row.nameId) === String(nameId) && String(row.id) !== String(excludeId)
  )
}

function formatAmountDisplay(amountType, amount) {
  const value = String(amount).trim()
  if (amountType === 'salary_percent') {
    return `${value}%`
  }
  return value
}

function fmtSalary(v) {
  const n = Number(v)
  if (!Number.isFinite(n)) return '—'
  return n.toFixed(2)
}

function clearCheckedMultiDeptUsers() {
  checkedMultiDeptUserIds.value = {}
}

function isMultiDeptUserChecked(id) {
  return !!checkedMultiDeptUserIds.value[String(id)]
}

function toggleMultiDeptUser(id, checked) {
  const key = String(id)
  const next = { ...checkedMultiDeptUserIds.value }
  if (checked) {
    next[key] = true
  } else {
    delete next[key]
  }
  checkedMultiDeptUserIds.value = next
}

function selectedMultiDeptUserIdsPayload() {
  return Object.keys(checkedMultiDeptUserIds.value)
    .map((id) => Number(id))
    .filter((id) => Number.isInteger(id) && id > 0)
}

function validateFiltersForScope() {
  if (filterForm.selectedMonth) {
    // Month selected — valid period
  } else if (filterForm.fromDate || filterForm.toDate) {
    if (!filterForm.fromDate || !filterForm.toDate) {
      return { valid: false, message: 'Select both From Date and To Date.' }
    }
    const from = new Date(`${filterForm.fromDate}T00:00:00`)
    const to = new Date(`${filterForm.toDate}T00:00:00`)
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
      return { valid: false, message: 'Invalid date range.' }
    }
    if (from >= to) {
      return { valid: false, message: 'From Date must be less than To Date.' }
    }
  } else {
    return { valid: false, message: 'Select a month or both From Date and To Date.' }
  }

  if (!filterForm.selectedBranchId) {
    return { valid: false, message: 'Select Branch is required.' }
  }

  const selectEntity = resolveSelectEntityForScope(filterForm.selectedBranchId, filterForm.selectEntity)
  const isAllBranchesScope =
    String(filterForm.selectedBranchId || '') === String(ALL_BRANCHES_SCOPE_VALUE)

  if (!isAllBranchesScope) {
    if (!selectEntity) {
      return { valid: false, message: 'Select Entity is required.' }
    }

    if (selectEntity === 'by_department') {
      if (!filterForm.departmentId) {
        return { valid: false, message: 'Select Department is required.' }
      }
    }

    if (selectEntity === 'by_name') {
      if (!String(filterForm.searchName || '').trim()) {
        return { valid: false, message: 'Search by name is required.' }
      }
      if (isSearchingUsers.value) {
        return { valid: false, message: 'Please wait while users are loading.' }
      }
      if (!searchResults.value.length) {
        return { valid: false, message: 'No users found for the search. Enter a valid name.' }
      }
    }
  }

  return { valid: true, message: '' }
}

const canLoadSavedEntries = computed(() => validateFiltersForScope().valid)

function validateFiltersForAdd() {
  return validateFiltersForScope()
}

function submitEntry() {
  if (isEditing.value) {
    updateEntry()
  } else {
    addEntry()
  }
}

function addEntry() {
  if (!canPrepareEarnDeduct.value) return

  const filterValidation = validateFiltersForAdd()
  if (!filterValidation.valid) {
    showToast(filterValidation.message, 'error')
    return
  }

  const nameId = String(form.name)
  if (isDuplicateName(nameId)) {
    showToast('This name is already in the list.', 'error')
    return
  }

  const entry = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    ...buildEntryFromForm()
  }

  emit('update:entries', [...props.entries, entry])
  persistEarnDeductPeriodFilters(filterForm)
  resetFormFields()
  showToast('Entry added to the list.', 'success')
}

function updateEntry() {
  if (!canPrepareEarnDeduct.value) return

  const nameId = String(form.name)
  if (isDuplicateName(nameId, editingEntryId.value)) {
    showToast('This name is already in the list.', 'error')
    return
  }

  const updatedEntry = {
    id: editingEntryId.value,
    ...buildEntryFromForm()
  }

  emit(
    'update:entries',
    props.entries.map((row) => (row.id === editingEntryId.value ? updatedEntry : row))
  )
  resetFormFields()
  showToast('Entry updated.', 'success')
}

function editEntry(row) {
  editingEntryId.value = row.id
  editingEntryNameLabel.value = row.nameLabel || ''
  populateFormFromEntry(row)
}

function deleteEntry(id) {
  const wasEditing = editingEntryId.value === id
  emit(
    'update:entries',
    props.entries.filter((row) => row.id !== id)
  )
  if (wasEditing) {
    resetFormFields()
  }
  showToast('Entry removed from the list.', 'success')
}

async function loadEarnDeductTypes() {
  isLoadingTypes.value = true
  try {
    const res = await apiFetch(earnDeductTypeApiPath.value)
    if (!res.ok) {
      earnDeductTypes.value = []
      return
    }
    const data = await res.json().catch(() => [])
    earnDeductTypes.value = Array.isArray(data) ? data : []
  } catch {
    earnDeductTypes.value = []
  } finally {
    isLoadingTypes.value = false
  }
}

function buildSavedListQueryParams() {
  const params = new URLSearchParams()
  if (filterForm.selectedMonth) {
    params.set('selectedMonth', String(filterForm.selectedMonth))
    params.set('year', String(new Date().getFullYear()))
  } else {
    params.set('fromDate', filterForm.fromDate)
    params.set('toDate', filterForm.toDate)
  }
  params.set('selectedBranchId', String(filterForm.selectedBranchId || ''))
  params.set(
    'selectEntity',
    resolveSelectEntityForScope(filterForm.selectedBranchId, filterForm.selectEntity)
  )
  if (filterForm.departmentId) {
    params.set('departmentId', String(filterForm.departmentId))
  }
  if (filterForm.searchName) {
    params.set('searchName', String(filterForm.searchName))
  }
  return params
}

let savedLoadTimer = null
let savedLoadRequestId = 0
let multiDeptLoadTimer = null
let multiDeptLoadRequestId = 0

async function loadMultiDepartmentUsers() {
  if (!canLoadMultiDepartmentUsers.value) {
    multiDeptUsers.value = []
    clearCheckedMultiDeptUsers()
    return
  }

  const filterValidation = validateFiltersForScope()
  if (!filterValidation.valid) {
    multiDeptUsers.value = []
    clearCheckedMultiDeptUsers()
    return
  }

  const requestId = ++multiDeptLoadRequestId
  isLoadingMultiDeptUsers.value = true
  try {
    const params = buildSavedListQueryParams()
    const res = await apiFetch(`${earnAndDeductConfigApiPath.value}/multi-department-users?${params.toString()}`)
    if (requestId !== multiDeptLoadRequestId) return
    if (!res.ok) {
      multiDeptUsers.value = []
      clearCheckedMultiDeptUsers()
      return
    }
    const data = await res.json().catch(() => [])
    const rows = Array.isArray(data) ? data : []
    multiDeptUsers.value = rows

    const allowedIds = new Set(rows.map((row) => String(row.id)))
    const nextChecked = {}
    for (const [id, checked] of Object.entries(checkedMultiDeptUserIds.value)) {
      if (checked && allowedIds.has(String(id))) {
        nextChecked[id] = true
      }
    }
    checkedMultiDeptUserIds.value = nextChecked
  } catch {
    if (requestId !== multiDeptLoadRequestId) return
    multiDeptUsers.value = []
    clearCheckedMultiDeptUsers()
  } finally {
    if (requestId === multiDeptLoadRequestId) {
      isLoadingMultiDeptUsers.value = false
    }
  }
}

function scheduleLoadMultiDepartmentUsers() {
  if (multiDeptLoadTimer) {
    window.clearTimeout(multiDeptLoadTimer)
  }
  multiDeptLoadTimer = window.setTimeout(() => {
    multiDeptLoadTimer = null
    loadMultiDepartmentUsers()
  }, 300)
}

function clearScheduledMultiDeptLoad() {
  if (multiDeptLoadTimer) {
    window.clearTimeout(multiDeptLoadTimer)
    multiDeptLoadTimer = null
  }
  multiDeptLoadRequestId += 1
}

async function loadSavedEntries() {
  const filterValidation = validateFiltersForScope()
  if (!filterValidation.valid) {
    savedEntries.value = []
    return
  }

  const requestId = ++savedLoadRequestId
  isLoadingSaved.value = true
  try {
    const params = buildSavedListQueryParams()
    const res = await apiFetch(`${earnAndDeductConfigApiPath.value}/saved?${params.toString()}`)
    if (requestId !== savedLoadRequestId) return
    if (!res.ok) {
      savedEntries.value = []
      return
    }
    const data = await res.json().catch(() => [])
    savedEntries.value = sortSavedEntriesByFullName(Array.isArray(data) ? data : [])
  } catch {
    if (requestId !== savedLoadRequestId) return
    savedEntries.value = []
  } finally {
    if (requestId === savedLoadRequestId) {
      isLoadingSaved.value = false
    }
  }
}

function scheduleLoadSavedEntries() {
  if (savedLoadTimer) {
    window.clearTimeout(savedLoadTimer)
  }
  savedLoadTimer = window.setTimeout(() => {
    savedLoadTimer = null
    loadSavedEntries()
  }, 300)
}

function clearScheduledSavedLoad() {
  if (savedLoadTimer) {
    window.clearTimeout(savedLoadTimer)
    savedLoadTimer = null
  }
  savedLoadRequestId += 1
}

async function saveAll() {
  if (!canPrepareEarnDeduct.value) return
  if (isSaving.value || props.entries.length === 0) return
  isSaving.value = true
  try {
    const payload = {
      selectedMonth: filterForm.selectedMonth || '',
      fromDate: filterForm.fromDate || '',
      toDate: filterForm.toDate || '',
      selectedBranchId: filterForm.selectedBranchId || '',
      selectEntity: resolveSelectEntityForScope(filterForm.selectedBranchId, filterForm.selectEntity),
      departmentId: filterForm.departmentId || '',
      searchName: filterForm.searchName || '',
      selectedMultiDeptUserIds: selectedMultiDeptUserIdsPayload(),
      entries: props.entries.map((row) => ({
        beneficiary: row.beneficiary,
        nameId: row.nameId,
        amountType: row.amountType,
        amount: row.amount,
        applicableFor: row.applicableFor
      }))
    }

    const res = await apiFetch(`${earnAndDeductConfigApiPath.value}/save`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })

    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      showToast(data?.message || 'Failed to save earn and deduct entries.', 'error')
      return
    }

    clearEarnDeductCurrentPeriod(filterForm)
    resetFormFields()
    showToast('Saved successfully.', 'success')
    await loadSavedEntries()
  } catch {
    showToast('Failed to save earn and deduct entries.', 'error')
  } finally {
    isSaving.value = false
  }
}

function buildApprovalPayload() {
  return {
    selectedMonth: filterForm.selectedMonth || '',
    fromDate: filterForm.fromDate || '',
    toDate: filterForm.toDate || '',
    year: new Date().getFullYear(),
    selectedBranchId: filterForm.selectedBranchId || '',
    selectEntity: resolveSelectEntityForScope(filterForm.selectedBranchId, filterForm.selectEntity),
    departmentId: filterForm.departmentId || '',
    searchName: filterForm.searchName || ''
  }
}

async function onApprovalAction(key) {
  const action = otherApprovalActions.value.find((a) => a.key === key)
  if (!action?.canClick || isUpdatingApproval.value) return

  const filterValidation = validateFiltersForScope()
  if (!filterValidation.valid) {
    showToast(filterValidation.message, 'error')
    return
  }

  if (!sortedSavedEntries.value.length) {
    showToast('No saved entries available for approval.', 'error')
    return
  }

  const confirmLabel = action.baseLabel || key
  if (!window.confirm(`Are you sure you want to record "${confirmLabel}"?`)) return

  isUpdatingApproval.value = true
  try {
    const res = await apiFetch(`${earnAndDeductConfigApiPath.value}/approval`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...buildApprovalPayload(),
        step: key
      })
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      showToast(data?.message || 'Failed to update approval.', 'error')
      return
    }
    const label = action.baseLabel || key
    showToast(`${label} recorded.`, 'success')
    await loadSavedEntries()
  } catch {
    showToast('Failed to update approval.', 'error')
  } finally {
    isUpdatingApproval.value = false
  }
}

onMounted(() => {
  loadEarnDeductTypes()
  scheduleLoadSavedEntries()
  scheduleLoadMultiDepartmentUsers()
})

onBeforeUnmount(() => {
  clearScheduledSavedLoad()
  clearScheduledMultiDeptLoad()
})
</script>

<style scoped>
.ead-entries-table {
  table-layout: fixed;
  width: 100%;
}

.ead-col {
  width: calc(100% / 6);
}

.ead-entries-table th,
.ead-entries-table td {
  padding: 0.45rem 0.65rem;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ead-entries-table thead tr {
  background: #f1f5f9;
}

.ead-entries-table th {
  font-size: 0.72rem;
  font-weight: 700;
  color: #1e293b;
}

.ead-entries-table td {
  font-size: 0.78rem;
  color: #334155;
}

.ead-entries-table td.actions {
  overflow: visible;
  white-space: nowrap;
}

.ead-saved-table {
  table-layout: fixed;
  width: 100%;
}

.ead-saved-col {
  width: calc(100% / 6);
}

.ead-empty-cell {
  text-align: center;
  color: #94a3b8;
  padding: 1.25rem 0.75rem;
  font-style: italic;
  white-space: normal;
}

.ead-editbtn {
  background: none;
  border: none;
  color: #2563eb;
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.85rem;
  padding: 0;
  display: flex;
  align-items: center;
}

.ead-delbtn {
  background: none;
  margin-left: 10px;
  border: none;
  color: red;
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.85rem;
  padding: 0;
  display: flex;
  align-items: center;
}

.ead-multi-dept-table th,
.ead-multi-dept-table td {
  padding: 0.45rem 0.65rem;
  text-align: left;
  white-space: nowrap;
}

.ead-multi-dept-table thead tr {
  background: #f1f5f9;
}

.ead-multi-dept-table th {
  font-size: 0.72rem;
  font-weight: 700;
  color: #1e293b;
}

.ead-multi-dept-table td {
  font-size: 0.78rem;
  color: #334155;
}

.ead-multi-dept-table th.num,
.ead-multi-dept-table td.num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.ead-multi-dept-table th.select-col,
.ead-multi-dept-table td.select-col {
  width: 4.5rem;
  text-align: center;
}

.ead-multi-dept-table .empty-cell {
  text-align: center;
  color: #94a3b8;
  font-style: italic;
  padding: 1.25rem 0.75rem;
  white-space: normal;
}
</style>
